/// <amd-module name="Artifacts/Package/components/UpstreamSourceDetailsPanel" />
import * as PackageRes from "Artifacts/Package/Resources";
import { registerModal } from "Artifacts/Root/containers/ModalManager";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import * as Res from "Artifacts/Root/Resources";
import { UpstreamVersionsDataVersionUpstream } from "Artifacts/Root/types/PackagingShared";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { Pill } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export const UpstreamSourceDetailsPanelId = "UpstreamSourceDetailsPanel";

export interface ISearchUpstreamsSourcesPanelProps {
  onDismiss: () => void;
  packageDisplayName: string;
  normalizedPackageVersion: string;
  upstreamData: UpstreamVersionsDataVersionUpstream[];
  ingestIsDisabled: boolean;
  versionIsDeleted: boolean;
}

interface IUpstreamSourceTableItem {
  upstreamData: UpstreamVersionsDataVersionUpstream;
}

const packageOrigin = [
  PackageRes.SearchUpstreamsUnknownSource,
  PackageRes.SearchUpstreamsExternalSource,
  PackageRes.SearchUpstreamsInternalSource,
];

const SearchUpstreamsSourcesPanel: React.FC<ISearchUpstreamsSourcesPanelProps> = ({
  onDismiss,
  packageDisplayName,
  normalizedPackageVersion,
  upstreamData,
  ingestIsDisabled,
  versionIsDeleted,
}) => {
  let columns = [
    {
      id: "upstream-name",
      name: Res.UpstreamSourceName,
      readonly: true,
      renderCell: renderUpstreamNames,
      width: new ObservableValue(-50),
      onSize: (event, index, width) => {
        columns = resizeColumn(columns, index, width);
      },
    } as ITableColumn<IUpstreamSourceTableItem>,
    {
      id: "origin",
      name: Res.Origin,
      readonly: true,
      renderCell: renderUpstreamOrigin,
      width: new ObservableValue(-50),
      onSize: (event, index, width) => {
        columns = resizeColumn(columns, index, width);
      },
    } as ITableColumn<IUpstreamSourceTableItem>,
  ];

  const tableItems = React.useMemo(() => {
    const items = upstreamData.map<IUpstreamSourceTableItem>((upstreams) => ({
      upstreamData: upstreams,
    }));
    return new ArrayItemProvider<IUpstreamSourceTableItem | undefined>(items);
  }, [upstreamData]);
  const packageExistsInUpstreamSources = tableItems.length > 0;
  return (
    <Panel
      className="search-upstreams-details-panel"
      titleProps={{
        text: Utils_String.format(
          PackageRes.SearchUpstreamsSourcesPanelTitle,
          packageDisplayName,
          normalizedPackageVersion,
        ),
      }}
      lightDismiss={false}
      onDismiss={onDismiss}
      footerButtonProps={[
        {
          onClick: onDismiss,
          text: Res.Modal_Cancel,
        },
      ]}
    >
      <div className="margin-vertical-8 padding-horizontal-20">
        {renderMessageCard(ingestIsDisabled, packageExistsInUpstreamSources, versionIsDeleted)}
        {packageExistsInUpstreamSources && <Table columns={columns} itemProvider={tableItems} />}
      </div>
    </Panel>
  );
};

function renderMessageCard(
  ingestIsDisabled: boolean,
  packageExistsInUpstreamSources: boolean,
  versionIsDeleted: boolean,
) {
  if (!packageExistsInUpstreamSources) {
    return (
      <MessageCard severity={MessageCardSeverity.Info}>
        {PackageRes.SearchUpstreamsSourcesPanelMessage_NoUpstreams}
      </MessageCard>
    );
  }

  if (versionIsDeleted) {
    return (
      <MessageCard severity={MessageCardSeverity.Info}>
        {PackageRes.SearchUpstreamsLocalDeletedMessage}
      </MessageCard>
    );
  }

  if (ingestIsDisabled) {
    return (
      <MessageCard severity={MessageCardSeverity.Info}>
        <FormatComponent format={PackageRes.SearchUpstreamsSourcesPanelMessage}>
          {PackageRes.UpstreamOverride}
          <Link
            href="https://go.microsoft.com/fwlink/?linkid=2196620&clcid=0x409"
            target="_blank"
            rel="nofollow noreferrer noopener help"
            underline
          >
            {PackageRes.LearnMore}
          </Link>
        </FormatComponent>
      </MessageCard>
    );
  }
}

function renderUpstreamNames(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamSourceTableItem>,
  tableItem: IUpstreamSourceTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <div className={"flex-row"}>
        <div className="upstream-name" key={tableItem.upstreamData.upstreamId}>
          {tableItem.upstreamData.upstreamName}
        </div>
      </div>
    </SimpleTableCell>
  );
}

function renderUpstreamOrigin(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUpstreamSourceTableItem>,
  tableItem: IUpstreamSourceTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <PillGroup className="flex-row" overflow={PillGroupOverflow.wrap}>
        <Pill key={tableItem.upstreamData.upstreamId}>
          {packageOrigin[tableItem.upstreamData.origin]}
        </Pill>
        {tableItem.upstreamData.isUpstreamForLocalVersion && (
          <Pill key={tableItem.upstreamData.upstreamId}>
            {PackageRes.SearchUpstreamsLocalVersion}
          </Pill>
        )}
      </PillGroup>
    </SimpleTableCell>
  );
}

registerModal(UpstreamSourceDetailsPanelId, SearchUpstreamsSourcesPanel);
